import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { autoUpdate, FloatingPortal, flip, offset, type Placement, shift, useFloating } from '@floating-ui/react';
import { Menu, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/solid';
import cx from 'classnames';

export interface DropdownMenuOption<T extends string> {
  value: T;
  label: string;
}

interface DropdownMenuProps<T extends string> {
  options: DropdownMenuOption<T>[];
  value: T;
  onChange: (value: T) => void;
  trigger: ReactNode;
  menuLabel: string;
  buttonAriaLabel: string;
  placement?: Placement;
  className?: string;
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

const TRIGGER_CLASS = 'flex cursor-pointer items-center gap-1.5 appearance-none border-none bg-transparent p-0';

export const DropdownMenu = <T extends string>({
  options,
  value,
  onChange,
  trigger,
  menuLabel,
  buttonAriaLabel,
  placement = 'bottom',
  className,
  buttonProps,
}: DropdownMenuProps<T>) => {
  const { refs, floatingStyles } = useFloating({
    placement,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  return (
    <Menu as="div" className={className}>
      <Menu.Button
        ref={refs.setReference}
        type="button"
        className={TRIGGER_CLASS}
        {...buttonProps}
        aria-label={buttonAriaLabel}
      >
        {trigger}
      </Menu.Button>

      <FloatingPortal>
        <Transition
          enter="transition-opacity ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Menu.Items
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-50 w-[153px] overflow-hidden rounded-3xl border border-black/5 bg-white pb-1 pt-1 shadow-[0_4px_6px_rgba(0,0,0,0.08)] focus:outline-none"
            aria-label={menuLabel}
          >
            <div className="px-4 pb-1 pt-2 text-[11px] leading-none text-black/50" aria-hidden="true">
              {menuLabel}
            </div>

            {options.map((option) => {
              const selected = value === option.value;

              return (
                <Menu.Item
                  key={option.value}
                  as="button"
                  type="button"
                  onClick={() => onChange(option.value)}
                  aria-current={selected ? 'true' : undefined}
                  className={({ active }: { active: boolean }) =>
                    cx(
                      'flex h-10 w-full items-center justify-between px-4 text-left text-sm font-medium tracking-[-0.14px] text-black',
                      { 'bg-black/5': active },
                    )
                  }
                >
                  <span>{option.label}</span>
                  {selected && <CheckIcon className="size-4 text-black/50" aria-hidden="true" />}
                </Menu.Item>
              );
            })}
          </Menu.Items>
        </Transition>
      </FloatingPortal>
    </Menu>
  );
};
